import { CCYearData } from "@easycv/common/types/conference";
import { User } from "@easycv/common/types/user";
import { CCSetup } from "./CCSetup";
import { CCViewingAs } from "./types/setup";

/**
  CCPermissions manages who can edit/view as different roles (mentee/mentor) of the CC.
  The ability to access the CC tab itself is determined by `src/customSections/career.js`
  After the tab has been accessed, permissions are managed by this class.

  There are three components that determine cc permissions:
    1. the cc's `status`
    2. the viewing user's `settings.conferenceTabAccess` value
    3. the viewing user's relationship to the cv owner

  The "viewing user" is the person who is logged into the portal.

  The "viewing user's relationship to the cv owner" will always be in one of three categories:
    1. One of the cv owner's mentors
    2. The cv owner themselves
    3. Someone in the same hierarchy as the cv owner with `conferenceTabAccess` of `"readOnly"` or `"editAccess"`
  In the third case, where someone is allowed access due to the value of `conferenceTabAccess`, 
  the viewer is acting as a "representitive" for the mentee.


    CTA: conferenceTabAccess

    CTA: editAccess-----------------> Treat as READ/WRITE
             |
             |
             v
  Is CV Owner AND own mentor--------> Treat as READ/WRITE
             |
             |
             v
        Is mentor-------------------> Treat as READ if status is mentee
             |                        Treat as READ/WRITE if status is mentor
             |
             v
       Is CV Owner------------------> Treat as READ/WRITE if status is mentee
             |                        Treat as READ if status is mentor
             |
             v
    CTA: readOnly ------------------> Treat as READ

  In older career conferences that do not opt into this CCPermissions system, there is slightly different logic:
    Generally, if the viewer has the "representitive" role as described above, a flag called `editAccessOverride`
    will be set to true, and that user will be allowed to edit as both the mentee and the mentor.
    This state also (theoretically) allows the user to edit conferences that are closed or finalized
    This logic is slightly inconsistent across older career conferences, and should not be relied on.

    To be specific, by "older career conferences", I mean anything that is not using cclib outside of the `manageSetup` function
*/
export class CCPermissions {
  private viewingAs: CCViewingAs;
  private parentState: CCSetup;
  private viewerAccess: Required<User["settings"]["conferenceTabAccess"]>;
  constructor(
    viewer: User,
    cvOwner: User,
    mentorList: string[],
    parentState: CCSetup
  ) {
    this.viewerAccess = viewer.settings?.conferenceTabAccess ?? "noAccess";
    if (viewer.id === cvOwner.id && mentorList.includes(viewer.id)) {
      // viewing as own mentor
      this.viewingAs = "own mentor";
    } else if (viewer.id === cvOwner.id) {
      // Viewing as mentee/cvowner
      this.viewingAs = "mentee";
    } else if (mentorList.includes(viewer.id)) {
      // Viewing as the mentor/reviewer
      this.viewingAs = "mentor";
    } else {
      if (this.viewerAccess === "noAccess") {
        // Shouldn't be possible
        throw new Error(
          "Failed to create a valid CCPermissions state. User should not be in the conference tab."
        );
      }
      this.viewingAs = "3rd party";
    }
    this.parentState = parentState;
  }

  private get parentStatus(): CCYearData["status"] {
    return this.parentState.newDef.career[this.parentState.viewYear].status;
  }

  private thirdPartyWrite() {
    return this.viewingAs === "3rd party" && this.viewerAccess === "editAccess";
  }
  private thirdPartyRead() {
    return (
      this.thirdPartyWrite() ||
      (this.viewingAs === "3rd party" && this.viewerAccess === "readOnly")
    );
  }

  public hasExplicitWriteAccess() {
    return this.viewerAccess === "editAccess";
  }
  public hasExplicitReadAccess() {
    return this.viewerAccess === "readOnly" || this.hasExplicitWriteAccess();
  }

  public readableAsMenteeIdentity() {
    return (
      this.viewingAs !== "3rd party" ||
      this.thirdPartyRead() ||
      this.hasExplicitReadAccess()
    );
  }
  public statusChangeableAsMenteeIdentity() {
    return (
      (this.parentStatus === "mentee" || this.parentStatus === "mentor") &&
      (this.viewingAs === "mentee" ||
        this.viewingAs === "own mentor" ||
        this.thirdPartyWrite() ||
        this.hasExplicitWriteAccess())
    );
  }
  public writableAsMenteeIdentity() {
    return (
      this.parentStatus === "mentee" &&
      (this.viewingAs === "mentee" ||
        this.viewingAs === "own mentor" ||
        this.thirdPartyWrite() ||
        this.hasExplicitWriteAccess())
    );
  }
  public readableAsMentorIdentity() {
    return (
      this.viewingAs === "mentor" ||
      this.viewingAs === "own mentor" ||
      this.thirdPartyRead() ||
      this.hasExplicitReadAccess()
    );
  }
  public statusChangeableAsMentorIdentity() {
    return (
      (this.parentStatus === "mentor" || this.parentStatus === "finalized") &&
      (this.viewingAs === "mentor" ||
        this.viewingAs === "own mentor" ||
        this.thirdPartyWrite() ||
        this.hasExplicitWriteAccess())
    );
  }
  public writableAsMentorIdentity() {
    return (
      this.parentStatus === "mentor" &&
      (this.viewingAs === "mentor" ||
        this.viewingAs === "own mentor" ||
        this.thirdPartyWrite() ||
        this.hasExplicitWriteAccess())
    );
  }
  public hasExplicitConferenceTabAccess() {
    return this.viewerAccess !== "noAccess";
  }
}
